import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  Check,
  ChevronRight,
  Loader2,
  ShoppingCart,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { getRouteState } from "@/lib/navigation";
import { calculateStickerPrice, formatGBP } from "@/lib/stickerPricing";
import { createShopifyCart } from "@/lib/api";

const FORMAT_SHOPIFY_MAP = {
  "die-cut":       { variantId: "57070853521790", handle: "die-cut-custom-stickers",    startingPrice: "\u00a30.60/unit" },
  "kiss-cut":      { variantId: "57077450670462", handle: "custom-stickers",            startingPrice: "\u00a33.00/unit" },
  "sticker-sheets":{ variantId: "57077450670462", handle: "custom-stickers",            startingPrice: "\u00a33.00/unit" },
  "product-labels":{ variantId: "57041743217022", handle: "product-labels-roll-of-500", startingPrice: "\u00a345/500" },
  "window-decals": { variantId: "57070858338686", handle: "window-decals",              startingPrice: "\u00a318.00" },
};

const SHOPIFY_DOMAIN = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN || "srbt1r-qn.myshopify.com";

function buildStickerQuoteHref({ format, size, material, finish, lamination, quantity }) {
  const params = new URLSearchParams();
  params.set("type", "Business Signage");
  if (size?.label) params.set("dimensions", size.label);
  if (quantity) params.set("quantity", String(quantity));
  const parts = [
    `${format?.label || "Sticker"} enquiry.`,
    size?.label ? `Size: ${size.label}.` : "",
    material ? `Material: ${material.label}.` : "",
    finish ? `Finish: ${finish}.` : "",
    lamination ? "Lamination requested." : "",
    quantity ? `Quantity: ${quantity}.` : "",
    "Artwork upload can follow on the quote form.",
  ].filter(Boolean);
  params.set("description", parts.join(" "));
  return `/request-quote?${params.toString()}`;
}

// ── Helper to remove nested ternary in StepBadge ──
const stepCircleCls = (done, active) => {
  if (done) return "bg-brand-gold text-brand-bg";
  if (active) return "border-2 border-brand-gold text-brand-gold bg-brand-gold/5";
  return "border border-white/20 text-brand-text-secondary/50";
};

function StepBadge({ number, label, active, done }) {
  return (
    <div className="flex items-center gap-3" data-testid={`sticker-step-badge-${number}`}>
      <span className={`flex h-8 w-8 items-center justify-center rounded-full text-[12px] font-bold transition-colors duration-300 ${stepCircleCls(done, active)}`}>
        {done ? <Check size={14} /> : number}
      </span>
      <span className={`text-[12px] tracking-[0.12em] uppercase font-semibold transition-colors duration-300 ${active || done ? "text-brand-text" : "text-brand-text-secondary/50"}`}>
        {label}
      </span>
    </div>
  );
}

export function StickerConfigurator({ selectedFormat, formatDef, pricingConfig }) {
  const location = useLocation();
  const configuratorRef = useRef(null);

  const [sizeMode, setSizeMode] = useState("preset");
  const [presetIndex, setPresetIndex] = useState(null);
  const [customW, setCustomW] = useState("");
  const [customH, setCustomH] = useState("");
  const [customUnit, setCustomUnit] = useState("mm");
  const [materialId, setMaterialId] = useState(null);
  const [finish, setFinish] = useState("matte");
  const [lamination, setLamination] = useState(false);
  const [quantity, setQuantity] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const formatConfig = useMemo(
    () => pricingConfig.formats?.find((f) => f.id === selectedFormat),
    [pricingConfig, selectedFormat]
  );

  useEffect(() => {
    if (!formatConfig) return;
    setPresetIndex(null);
    setSizeMode("preset");
    setCustomW("");
    setCustomH("");
    setMaterialId(formatConfig.materials?.[0] || "white-vinyl");
    setFinish(formatConfig.finishes?.[0] || "matte");
    setLamination(false);
    setQuantity(String(formatConfig.default_quantity || 100));
  }, [formatConfig]);

  const availableMaterials = useMemo(() => {
    if (!formatConfig) return [];
    return (pricingConfig.materials || []).filter((m) =>
      formatConfig.materials?.includes(m.id)
    );
  }, [pricingConfig, formatConfig]);

  const areaSqcm = useMemo(() => {
    if (!formatConfig) return 0;
    if (sizeMode === "preset" && presetIndex !== null) {
      return formatConfig.preset_sizes?.[presetIndex]?.area_sqcm || 0;
    }
    if (sizeMode === "custom" && customW && customH) {
      const w = parseFloat(customW);
      const h = parseFloat(customH);
      if (w > 0 && h > 0) {
        return customUnit === "mm" ? (w * h) / 100 : w * h;
      }
    }
    return 0;
  }, [formatConfig, sizeMode, presetIndex, customW, customH, customUnit]);

  const hasSizeSelected = sizeMode === "preset" ? presetIndex !== null : areaSqcm > 0;
  const sizeLabel = useMemo(() => {
    if (sizeMode === "preset" && presetIndex !== null && formatConfig) {
      return formatConfig.preset_sizes?.[presetIndex]?.label || "";
    }
    if (sizeMode === "custom" && customW && customH) {
      return `${customW} \u00d7 ${customH}${customUnit}`;
    }
    return "";
  }, [sizeMode, presetIndex, formatConfig, customW, customH, customUnit]);

  const calc = useCallback(
    (opts) => calculateStickerPrice(opts, pricingConfig),
    [pricingConfig]
  );

  const livePrice = useMemo(() => {
    if (!selectedFormat || !areaSqcm || areaSqcm <= 0) return null;
    return calc({
      formatId: selectedFormat,
      areaSqcm,
      materialId: materialId || "white-vinyl",
      lamination,
      quantity: Math.max(1, parseInt(quantity, 10) || 1),
    });
  }, [selectedFormat, areaSqcm, materialId, lamination, quantity, calc]);

  const presetPrices = useMemo(() => {
    if (!formatConfig) return [];
    const qty = Math.max(1, parseInt(quantity, 10) || formatConfig.default_quantity || 100);
    return (formatConfig.preset_sizes || []).map((s) =>
      calc({ formatId: selectedFormat, areaSqcm: s.area_sqcm, materialId: materialId || "white-vinyl", lamination, quantity: qty }).unitPrice
    );
  }, [formatConfig, selectedFormat, materialId, lamination, quantity, calc]);

  const materialInfo = useMemo(
    () => pricingConfig.materials?.find((m) => m.id === materialId),
    [pricingConfig, materialId]
  );

  const quoteHref = buildStickerQuoteHref({
    format: formatDef,
    size: sizeMode === "preset" && presetIndex !== null ? formatConfig?.preset_sizes?.[presetIndex] : { label: sizeLabel },
    material: materialInfo,
    finish,
    lamination,
    quantity,
  });

  const shopifyProduct = selectedFormat ? FORMAT_SHOPIFY_MAP[selectedFormat] || null : null;

  const handleStickerCheckout = async () => {
    if (!shopifyProduct || checkoutLoading) return;
    setCheckoutLoading(true);
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    const note = [
      formatDef?.label || selectedFormat,
      sizeLabel ? `Size: ${sizeLabel}` : null,
      materialInfo?.label ? `Material: ${materialInfo.label}` : null,
      finish ? `Finish: ${finish}` : null,
      lamination ? "Lamination" : null,
      quantity ? `Qty: ${quantity}` : null,
    ].filter(Boolean).join(" | ");
    try {
      const result = await createShopifyCart({ variantId: shopifyProduct.variantId, quantity: qty, handle: shopifyProduct.handle, note });
      window.open(result.checkout_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("[StickerConfigurator] cart creation failed, using direct URL:", err);
      window.open(`https://${SHOPIFY_DOMAIN}/products/${shopifyProduct.handle}`, "_blank", "noopener,noreferrer");
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!selectedFormat || !formatConfig) return null;

  return (
    <section ref={configuratorRef} className="mt-16" data-testid="sticker-configurator-section">
      <div className="mb-4">
        <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-gold">
          Configure your {formatDef?.label?.toLowerCase() || "stickers"}
        </p>
        <h2 className="mt-2 font-heading text-2xl sm:text-3xl tracking-tight text-brand-text">
          {formatConfig.tagline}
        </h2>
        <p className="mt-2 text-[13px] text-brand-text-secondary/60 max-w-2xl">{formatConfig.description}</p>
      </div>

      {/* Step progress */}
      <div className="flex items-center gap-4 sm:gap-6 mb-10 py-4 border-b border-white/[0.06] overflow-x-auto" data-testid="sticker-steps-bar">
        <StepBadge number={2} label="Size" active={!hasSizeSelected} done={hasSizeSelected} />
        <ChevronRight size={14} className="text-white/20 shrink-0" />
        <StepBadge number={3} label="Material" active={hasSizeSelected && !!materialId} done={hasSizeSelected && !!materialId} />
        <ChevronRight size={14} className="text-white/20 shrink-0" />
        <StepBadge number={4} label="Quantity" active={!!materialId} done={!!quantity && parseInt(quantity, 10) > 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8 items-start">
        {/* ── Left: Steps ── */}
        <div className="space-y-8">
          {/* STEP 2: Size */}
          <div className="rounded-sm border border-white/[0.08] bg-white/[0.02] p-6 md:p-7" data-testid="sticker-step-size">
            <div className="flex items-center gap-3 mb-6">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-gold text-[13px] font-bold text-brand-bg">2</span>
              <span className="text-[15px] font-semibold text-brand-text tracking-tight">Choose a size</span>
            </div>
            <div className="flex gap-2 mb-6" data-testid="sticker-size-mode-toggle">
              <button type="button" onClick={() => setSizeMode("preset")} className={`rounded-sm px-5 py-2.5 text-[13px] font-medium transition-all duration-200 ${sizeMode === "preset" ? "bg-brand-gold text-brand-bg" : "border border-white/15 text-brand-text-secondary/70 hover:text-brand-text hover:border-white/30"}`} data-testid="sticker-size-mode-preset">Common sizes</button>
              <button type="button" onClick={() => setSizeMode("custom")} className={`rounded-sm px-5 py-2.5 text-[13px] font-medium transition-all duration-200 ${sizeMode === "custom" ? "bg-brand-gold text-brand-bg" : "border border-white/15 text-brand-text-secondary/70 hover:text-brand-text hover:border-white/30"}`} data-testid="sticker-size-mode-custom">Custom size</button>
            </div>
            {sizeMode === "preset" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="sticker-preset-sizes">
                {(formatConfig.preset_sizes || []).map((preset, i) => (
                  <button key={preset.label} type="button" onClick={() => setPresetIndex(i)} className={`rounded-sm border px-5 py-4 text-left transition-all duration-200 ${presetIndex === i ? "border-brand-gold bg-brand-gold/10 ring-1 ring-brand-gold/30 shadow-[0_0_12px_rgba(203,160,82,0.08)]" : "border-white/10 bg-white/[0.01] hover:border-white/25 hover:bg-white/[0.03]"}`} data-testid={`sticker-preset-size-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-[15px] font-semibold ${presetIndex === i ? "text-brand-gold" : "text-brand-text"}`}>{preset.label}</span>
                      <span className={`text-[13px] font-semibold ${presetIndex === i ? "text-brand-gold/70" : "text-brand-text-secondary/40"}`}>{formatGBP(presetPrices[i] || 0)}/ea</span>
                    </div>
                    <span className={`block text-[12px] mt-1 ${presetIndex === i ? "text-brand-gold/60" : "text-brand-text-secondary/50"}`}>{preset.note}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="rounded-sm border border-white/[0.08] bg-white/[0.015] p-5" data-testid="sticker-custom-size-inputs">
                <div className="grid grid-cols-2 gap-4">
                  <label>
                    <span className="block text-[12px] font-medium tracking-wide text-brand-text-secondary/70 mb-2">Width</span>
                    <input type="number" min="0" inputMode="decimal" value={customW} onChange={(e) => setCustomW(e.target.value)} placeholder={customUnit === "mm" ? "e.g. 70" : "e.g. 7"} className="w-full bg-white/[0.04] border border-white/10 focus:border-brand-gold rounded-sm text-brand-text px-3.5 py-3 outline-none transition-colors text-[15px] placeholder:text-brand-text-secondary/30" data-testid="sticker-custom-w" />
                  </label>
                  <label>
                    <span className="block text-[12px] font-medium tracking-wide text-brand-text-secondary/70 mb-2">Height</span>
                    <input type="number" min="0" inputMode="decimal" value={customH} onChange={(e) => setCustomH(e.target.value)} placeholder={customUnit === "mm" ? "e.g. 70" : "e.g. 7"} className="w-full bg-white/[0.04] border border-white/10 focus:border-brand-gold rounded-sm text-brand-text px-3.5 py-3 outline-none transition-colors text-[15px] placeholder:text-brand-text-secondary/30" data-testid="sticker-custom-h" />
                  </label>
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <span className="text-[12px] text-brand-text-secondary/50">Unit:</span>
                  <div className="inline-flex rounded-sm border border-white/10 overflow-hidden" data-testid="sticker-custom-unit-toggle">
                    {["mm", "cm"].map((u) => (
                      <button key={u} type="button" onClick={() => setCustomUnit(u)} className={`px-4 py-2 text-[12px] font-medium transition-all ${customUnit === u ? "bg-brand-gold text-brand-bg" : "text-brand-text-secondary/60 hover:text-brand-text"}`} data-testid={`sticker-custom-unit-${u}`}>{u}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* STEP 3: Material & Finish */}
          <div className="rounded-sm border border-white/[0.08] bg-white/[0.02] p-6 md:p-7" data-testid="sticker-step-material">
            <div className="flex items-center gap-3 mb-6">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-gold text-[13px] font-bold text-brand-bg">3</span>
              <span className="text-[15px] font-semibold text-brand-text tracking-tight">Material &amp; finish</span>
            </div>
            <div className="mb-6">
              <p className="text-[12px] font-medium text-brand-text-secondary/70 mb-3">Material</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="sticker-material-cards">
                {availableMaterials.map((mat) => {
                  const isActive = materialId === mat.id;
                  return (
                    <button key={mat.id} type="button" onClick={() => setMaterialId(mat.id)} className={`rounded-sm border p-4 text-left transition-all duration-200 ${isActive ? "border-brand-gold bg-brand-gold/10 ring-1 ring-brand-gold/30" : "border-white/10 bg-white/[0.01] hover:border-white/25"}`} data-testid={`sticker-material-${mat.id}`}>
                      <p className={`text-[14px] font-semibold ${isActive ? "text-brand-gold" : "text-brand-text"}`}>{mat.label}</p>
                      <p className={`mt-1 text-[12px] ${isActive ? "text-brand-gold/60" : "text-brand-text-secondary/50"}`}>{mat.short}</p>
                      {mat.surcharge_pct > 0 && (
                        <p className="mt-1 text-[10px] text-brand-text-secondary/35">+{mat.surcharge_pct}% material surcharge</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-[12px] font-medium text-brand-text-secondary/70 mb-3">Finish</p>
                <div className="flex gap-2" data-testid="sticker-finish-options">
                  {(formatConfig.finishes || ["matte", "gloss"]).map((f) => (
                    <button key={f} type="button" onClick={() => setFinish(f)} className={`rounded-sm border px-5 py-2.5 text-[13px] font-medium capitalize transition-all duration-200 ${finish === f ? "border-brand-gold bg-brand-gold/10 text-brand-gold ring-1 ring-brand-gold/20" : "border-white/10 text-brand-text-secondary/65 hover:text-brand-text hover:border-white/25"}`} data-testid={`sticker-finish-${f}`}>{f}</button>
                  ))}
                </div>
              </div>
              {formatConfig.has_lamination && (
                <div>
                  <p className="text-[12px] font-medium text-brand-text-secondary/70 mb-3">Lamination</p>
                  <label className="flex items-center gap-3 cursor-pointer rounded-sm border border-white/[0.06] bg-white/[0.015] px-4 py-3 hover:border-white/15 transition-colors" data-testid="sticker-lamination-group">
                    <input type="checkbox" checked={lamination} onChange={(e) => setLamination(e.target.checked)} className="h-4 w-4 accent-[#CBA052] shrink-0" data-testid="sticker-lamination-checkbox" />
                    <span>
                      <span className="block text-[13px] font-medium text-brand-text">Add lamination</span>
                      <span className="block text-[11px] text-brand-text-secondary/45 mt-0.5">+{pricingConfig.lamination_surcharge_pct || 15}% — extra durability and finish.</span>
                    </span>
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* STEP 4: Quantity */}
          <div className="rounded-sm border border-white/[0.08] bg-white/[0.02] p-6 md:p-7" data-testid="sticker-step-quantity">
            <div className="flex items-center gap-3 mb-6">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-gold text-[13px] font-bold text-brand-bg">4</span>
              <span className="text-[15px] font-semibold text-brand-text tracking-tight">Quantity</span>
            </div>
            <div className="flex items-end gap-4 mb-4">
              <label className="flex-1 max-w-[200px]" data-testid="sticker-quantity-group">
                <span className="block text-[12px] font-medium text-brand-text-secondary/70 mb-2">How many?</span>
                <input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="w-full bg-white/[0.04] border border-white/10 focus:border-brand-gold rounded-sm text-brand-text px-3.5 py-3 outline-none transition-colors text-[15px]" data-testid="sticker-quantity-input" />
              </label>
              {livePrice?.qtyLabel && (
                <span className="text-[12px] text-brand-text-secondary/45 pb-3">Tier: {livePrice.qtyLabel}</span>
              )}
            </div>
            <div className="rounded-sm border border-white/[0.05] bg-white/[0.01] p-4" data-testid="sticker-qty-tiers">
              <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-brand-text-secondary/50 mb-3">Volume pricing</p>
              <div className="flex flex-wrap gap-2">
                {(pricingConfig.quantity_tiers || []).map((tier) => {
                  const qty = parseInt(quantity, 10) || 0;
                  const isActive = qty >= tier.min && qty <= tier.max;
                  const savings = tier.multiplier < 1 ? Math.round((1 - tier.multiplier) * 100) : 0;
                  return (
                    <div key={tier.label} className={`rounded-sm border px-3 py-2 text-[11px] transition-colors ${isActive ? "border-brand-gold bg-brand-gold/10 text-brand-gold font-semibold" : "border-white/[0.06] text-brand-text-secondary/45"}`} data-testid={`sticker-tier-${tier.min}`}>
                      {tier.label}
                      {savings > 0 && <span className="ml-1 text-green-400">-{savings}%</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ── Right: Live Summary (sticky on desktop) ── */}
        <div className="lg:sticky lg:top-28" data-testid="sticker-quote-summary">
          <div className="rounded-sm border border-brand-gold/20 bg-[linear-gradient(135deg,rgba(203,160,82,0.04),rgba(10,13,20,0.97))] p-6 md:p-7 shadow-[0_0_24px_rgba(203,160,82,0.04)]">
            <p className="text-[12px] tracking-[0.16em] uppercase font-bold text-brand-gold mb-6">Your {formatDef?.label || "Sticker"} Summary</p>
            <div className="space-y-4">
              {[
                { label: "Format", value: formatDef?.label || "\u2014" },
                { label: "Size", value: sizeLabel || "Select a size" },
                { label: "Material", value: materialInfo?.label || "\u2014" },
                { label: "Finish", value: finish ? finish.charAt(0).toUpperCase() + finish.slice(1) : "\u2014" },
                ...(lamination ? [{ label: "Extra", value: "Lamination" }] : []),
                { label: "Qty", value: quantity || "\u2014" },
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-4 border-b border-white/[0.06] pb-3 last:border-0 last:pb-0" data-testid={`sticker-summary-${row.label.toLowerCase()}`}>
                  <span className="text-[12px] uppercase tracking-[0.12em] text-brand-text-secondary/55 font-medium">{row.label}</span>
                  <span className={`text-[14px] font-semibold text-right ${row.value === "Select a size" || row.value === "\u2014" ? "text-brand-text-secondary/30 font-normal" : "text-brand-text"}`}>{row.value}</span>
                </div>
              ))}
            </div>
            {livePrice && (
              <div className="mt-6 rounded-sm border border-brand-gold/15 bg-brand-gold/[0.04] p-4" data-testid="sticker-live-price">
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.12em] text-brand-text-secondary/50 font-medium">Estimated price</p>
                    <p className="mt-1 text-[28px] font-bold text-brand-gold leading-none">{formatGBP(livePrice.unitPrice)}<span className="text-[14px] font-normal text-brand-gold/60">/unit</span></p>
                    {parseInt(quantity, 10) > 1 && (
                      <p className="mt-1 text-[13px] text-brand-text-secondary/55">
                        {quantity} units = <span className="text-brand-text font-semibold">{formatGBP(livePrice.totalPrice)}</span>
                      </p>
                    )}
                  </div>
                  {livePrice.qtyMultiplier < 1 && (
                    <span className="rounded-full bg-green-500/15 border border-green-500/25 px-2.5 py-1 text-[10px] font-bold text-green-400 uppercase tracking-wider">
                      {Math.round((1 - livePrice.qtyMultiplier) * 100)}% off
                    </span>
                  )}
                </div>
                <p className="mt-2 text-[10px] text-brand-text-secondary/35">Indicative. Final quote confirmed after artwork review.</p>
              </div>
            )}
            <Link to={quoteHref} state={getRouteState(location)} className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-sm bg-brand-gold px-7 py-4 text-[14px] font-bold tracking-wide text-brand-bg transition-all duration-300 hover:bg-brand-gold-hover hover:shadow-[0_0_20px_rgba(203,160,82,0.2)]" data-testid="sticker-request-quote-cta">
              Request Quote <ArrowRight size={16} />
            </Link>
            {shopifyProduct && (
              <button type="button" onClick={handleStickerCheckout} disabled={checkoutLoading} className="mt-3 flex w-full items-center justify-center gap-2.5 rounded-sm border border-white/15 px-7 py-3.5 text-[13px] font-medium tracking-wide text-brand-text-secondary transition-all duration-300 hover:border-brand-gold/40 hover:text-brand-text disabled:opacity-60" data-testid="sticker-shopify-cta">
                {checkoutLoading
                  ? <><Loader2 size={14} className="animate-spin" /> Preparing...</>
                  : <><ShoppingCart size={14} /> Order via Shopify &mdash; from {shopifyProduct.startingPrice}</>
                }
              </button>
            )}
            <p className="mt-4 text-center text-[12px] text-brand-text-secondary/45" data-testid="sticker-summary-note">
              Not sure on every detail? Send what you know — we'll guide the rest.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
